// ============================================================================
// AdminEnrollmentRecordExternalBatchPanel — record externally-executed AMP
// agreements for a WAVE of producers (Build S6b, Task #37).
// ============================================================================
// Route: #admin/enrollments/record-external-batch. Sidebar pinned to
// Enrollments. UIF only. Lists every pre-`enrolled` AMP enrollment; each card
// collects execution metadata + PDF uploads (enrollment-scoped, shared
// two-phase flow). "Record & Enroll All Ready Rows" POSTs the ready rows to the
// batch endpoint, which processes EACH in its own transaction (partial-failure
// isolation) and returns a per-row result.
// ============================================================================

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, AlertCircle, ArrowLeft, CheckCircle2, XCircle, UploadCloud, Users,
} from 'lucide-react';
import {
  api, type EnrollmentSummary, type ExternalExecutionChannel,
  type RecordExternalBatchRow, type RecordExternalBatchResult,
} from '../api';
import { uploadDocument } from '../lib/documentUpload';
import { businessToday } from '../lib/businessDate';

interface Props { token: string; }

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PRE_ENROLLED = 'invited,application_started,application_submitted,under_review,conditionally_approved';
const inputCls =
  'w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none';

const CHANNEL_LABELS: Record<ExternalExecutionChannel, string> = {
  external_docusign: 'DocuSign (off-platform)',
  external_paper: 'Paper / wet signature',
  external_email: 'Emailed signed PDF',
};

interface RowState {
  executionDate: string;
  channel: ExternalExecutionChannel;
  docusignUuid: string;
  signersText: string;      // comma-separated for batch entry
  agreementId: string | null;
  certId: string | null;
  vocId: string | null;
  vocWaived: boolean;
  vocWaiverReason: string;
}
const emptyRow = (): RowState => ({
  executionDate: '', channel: 'external_docusign', docusignUuid: '',
  signersText: '', agreementId: null, certId: null, vocId: null,
  vocWaived: false, vocWaiverReason: '',
});

function signersOf(rs: RowState): string[] {
  return rs.signersText.split(',').map((s) => s.trim()).filter(Boolean);
}

function rowReady(rs: RowState, vocOnFile: boolean, today: string): boolean {
  const uuidOk = !rs.docusignUuid.trim() || UUID_RE.test(rs.docusignUuid.trim());
  const certOk = rs.channel !== 'external_docusign' || !!rs.certId;
  // A waived row satisfies the VOC leg only WITH a reason — mirrors the
  // server's 422, so the row can't be submitted into a guaranteed rejection.
  const vocOk = vocOnFile || !!rs.vocId || (rs.vocWaived && !!rs.vocWaiverReason.trim());
  return !!rs.executionDate && rs.executionDate <= today
    && signersOf(rs).length >= 1 && uuidOk && !!rs.agreementId && certOk && vocOk;
}

// Compact per-cell upload control for the batch table.
function SlotButton({
  token, farmId, scopeId, typeCode, docTitle, label, required, uploadedId, onUploaded,
}: {
  token: string; farmId: number; scopeId: string; typeCode: string; docTitle: string;
  label: string; required: boolean; uploadedId: string | null; onUploaded: (id: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState('');
  const [err, setErr] = useState('');
  async function pick(f: File) {
    setErr('');
    try {
      const id = await uploadDocument(token, f, {
        title: docTitle, type_code: typeCode, scope_type: 'enrollment', scope_id: scopeId, farm_id: farmId,
      }, setPhase);
      onUploaded(id);
    } catch (e: any) { setErr(e?.message ?? 'Upload failed'); }
    // No finally { setPhase('') }: uploadDocument() owns phase cleanup in its
    // own finally (Copilot #17 r6). Two owners for one piece of state drifts.
  }
  const tone = uploadedId ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : required ? 'border-amber-300 text-amber-700' : 'border-gray-300 text-gray-600';
  return (
    <div>
      {/* The hidden input this button drives. Its absence was the defect
          (Copilot #16 r2-1, flagged twice): the button called
          ref.current?.click() against a ref bound to nothing, so `ref.current`
          was permanently null, the click was a silent no-op, pick() was
          unreachable, and no batch row could ever satisfy the required
          agreement_document_id. Uploads in this panel had never worked.
          value is cleared after each pick so re-selecting the SAME file still
          fires onChange (the browser suppresses it otherwise). */}
      <input
        ref={ref}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(ev) => {
          const f = ev.target.files?.[0];
          ev.target.value = '';
          if (f) void pick(f);
        }}
      />
      <button type="button" disabled={!!phase} onClick={() => ref.current?.click()}
        className={'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs disabled:opacity-50 ' + tone}>
        {phase ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : uploadedId ? <CheckCircle2 className="w-3.5 h-3.5" /> : <UploadCloud className="w-3.5 h-3.5" />}
        {label}{required && !uploadedId ? ' *' : ''}
      </button>
      {err && <div className="text-[11px] text-red-600 mt-0.5 max-w-[12rem]">{err}</div>}
    </div>
  );
}

export function AdminEnrollmentRecordExternalBatchPanel({ token }: Props) {
  const [list, setList] = useState<EnrollmentSummary[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [vocOnFile, setVocOnFile] = useState<Record<string, boolean>>({});
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [results, setResults] = useState<Record<string, RecordExternalBatchResult>>({});
  const [busy, setBusy] = useState(false);

  // UIF business date (America/Chicago), per David's 2026-07-20 program-wide
  // ruling. Must match the API's guard exactly — using the browser's local day
  // here is what produced 422s for operators ahead of UTC.
  const today = businessToday();

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const res = await api.adminEnrollments(token, { status: PRE_ENROLLED });
      setList(res.enrollments);
      setRows((prev) => {
        const next = { ...prev };
        for (const e of res.enrollments) if (!next[e.enrollment_id]) next[e.enrollment_id] = emptyRow();
        return next;
      });
      // Per-enrollment VOC-on-file (a handful of rows) — informs slot requirement.
      const vocPairs = await Promise.all(
        res.enrollments.map(async (e) => {
          try { const v = await api.adminEnrollmentVocOnFile(token, e.enrollment_id); return [e.enrollment_id, v.on_file] as const; }
          catch { return [e.enrollment_id, false] as const; }
        }),
      );
      setVocOnFile(Object.fromEntries(vocPairs));
    } catch (err: any) {
      setLoadErr(err?.message ?? 'Could not load enrollments');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const back = () => { window.location.hash = '#admin/enrollments'; };
  const patch = (id: string, p: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyRow()), ...p } }));

  const readyIds = (list ?? [])
    .filter((e) => !results[e.enrollment_id]?.success)
    .filter((e) => rowReady(rows[e.enrollment_id] ?? emptyRow(), !!vocOnFile[e.enrollment_id], today))
    .map((e) => e.enrollment_id);

  const submit = async () => {
    if (readyIds.length === 0 || busy) return;
    setBusy(true);
    try {
      const payload: RecordExternalBatchRow[] = readyIds.map((id) => {
        const rs = rows[id];
        return {
          enrollment_id: id,
          execution_date: rs.executionDate,
          execution_channel: rs.channel,
          docusign_envelope_uuid: rs.docusignUuid.trim() || null,
          signer_names: signersOf(rs),
          agreement_document_id: rs.agreementId!,
          certificate_document_id: rs.certId,
          // null while waived, matching the per-producer panel. Sending both a
          // waiver and a document produced a record that was simultaneously
          // waived and VOC-backed, and advanced that document as "consumed" by
          // a flow that declined to consume it. The API now rejects the
          // combination with 422 voc_waiver_conflict; this keeps the UI from
          // sending a request it knows will be refused.
          voc_document_id: rs.vocWaived ? null : rs.vocId,
          ...(rs.vocWaived
            ? { voc_waived: true, voc_waiver_reason: rs.vocWaiverReason.trim() }
            : {}),
        };
      });
      const res = await api.adminEnrollmentRecordExternalBatch(token, payload);
      setResults((prev) => {
        const next = { ...prev };
        for (const r of res.results) next[r.enrollment_id] = r;
        return next;
      });
    } catch (err: any) {
      setLoadErr(err?.message ?? 'Batch submit failed');
    } finally {
      setBusy(false);
    }
  };

  if (loadErr && !list) {
    return (
      <div className="space-y-4">
        <BackLink onClick={back} />
        <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4"><AlertCircle className="w-5 h-5 shrink-0" /> {loadErr}</div>
      </div>
    );
  }
  if (!list) return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      <BackLink onClick={back} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-6 h-6 text-green-700" />
          <h2 className="text-xl font-bold text-gray-900">Record external enrollment wave</h2>
        </div>
        <button type="button" disabled={readyIds.length === 0 || busy} onClick={() => void submit()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Record &amp; Enroll All Ready Rows ({readyIds.length})
        </button>
      </div>

      {loadErr && <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-3 text-sm"><AlertCircle className="w-4 h-4 shrink-0" /> {loadErr}</div>}

      {list.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white rounded-xl border border-gray-200 p-6">No pre-enrolled AMP producers pending.</p>
      ) : (
        <div className="space-y-3">
          {list.map((e) => {
            const rs = rows[e.enrollment_id] ?? emptyRow();
            const result = results[e.enrollment_id];
            const vocHere = !!vocOnFile[e.enrollment_id];
            const ready = rowReady(rs, vocHere, today);
            const uuidOk = !rs.docusignUuid.trim() || UUID_RE.test(rs.docusignUuid.trim());
            const docTitle = (kind: string) => `${e.farm_code} — ${kind}`;

            return (
              <div key={e.enrollment_id}
                className={'bg-white rounded-xl border shadow-sm p-3 ' + (result?.success ? 'border-emerald-200' : result ? 'border-red-200' : ready ? 'border-green-200' : 'border-gray-200')}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-mono text-xs text-gray-500 mr-2">{e.farm_code}</span>
                    <span className="font-medium text-gray-900">{e.farm_name}</span>
                    <span className="ml-2 text-xs text-gray-500">{e.program_code} {e.year_no} · {e.status.replace(/_/g, ' ')}</span>
                  </div>
                  {result
                    ? (result.success
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-4 h-4" /> enrolled</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700" title={result.error ?? ''}><XCircle className="w-4 h-4" /> {result.code ?? 'failed'}</span>)
                    : ready
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"><CheckCircle2 className="w-4 h-4" /> ready</span>
                      : <span className="text-xs text-gray-400">incomplete</span>}
                </div>

                {!result?.success && (
                  <div className="mt-3 grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
                    <label className="lg:col-span-2 block">
                      <Small>Execution date *</Small>
                      <input type="date" max={today} value={rs.executionDate} onChange={(ev) => patch(e.enrollment_id, { executionDate: ev.target.value })} className={inputCls} />
                    </label>
                    <label className="lg:col-span-3 block">
                      <Small>Channel</Small>
                      <select value={rs.channel} onChange={(ev) => patch(e.enrollment_id, { channel: ev.target.value as ExternalExecutionChannel })} className={inputCls}>
                        {(Object.keys(CHANNEL_LABELS) as ExternalExecutionChannel[]).map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>)}
                      </select>
                    </label>
                    <label className="lg:col-span-3 block">
                      <Small>DocuSign UUID</Small>
                      <input value={rs.docusignUuid} onChange={(ev) => patch(e.enrollment_id, { docusignUuid: ev.target.value })}
                        placeholder="optional" className={inputCls + (uuidOk ? '' : ' border-red-400')} />
                    </label>
                    <label className="lg:col-span-4 block">
                      <Small>Signer name(s), comma-separated *</Small>
                      <input value={rs.signersText} onChange={(ev) => patch(e.enrollment_id, { signersText: ev.target.value })} className={inputCls} />
                    </label>

                    <div className="lg:col-span-12 flex flex-wrap items-center gap-2 pt-1">
                      <SlotButton token={token} farmId={e.farm_id} scopeId={e.enrollment_id} typeCode="AMP_AGREEMENT"
                        docTitle={docTitle('Executed AMP agreement')} label="Agreement" required uploadedId={rs.agreementId}
                        onUploaded={(id) => patch(e.enrollment_id, { agreementId: id })} />
                      <SlotButton token={token} farmId={e.farm_id} scopeId={e.enrollment_id} typeCode="EXECUTION_CERTIFICATE"
                        docTitle={docTitle('Execution certificate')} label="Certificate" required={rs.channel === 'external_docusign'} uploadedId={rs.certId}
                        onUploaded={(id) => patch(e.enrollment_id, { certId: id })} />
                      <SlotButton token={token} farmId={e.farm_id} scopeId={e.enrollment_id} typeCode="VOC_ASSESSMENT"
                        docTitle={docTitle('VOC assessment')} label={vocHere ? 'VOC (on file)' : 'VOC'}
                        required={!vocHere && !rs.vocWaived} uploadedId={rs.vocId}
                        onUploaded={(id) => patch(e.enrollment_id, { vocId: id })} />
                    </div>

                    {/* Wave 2 item 3: operator VOC waiver. Only offered when the farm
                        has no VOC on file — waiving an existing VOC is meaningless.
                        The reason is mandatory; the server returns 422 without it, so
                        rowReady() withholds the row until it is supplied. */}
                    {!vocHere && (
                      <div className="lg:col-span-12 flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                          <input type="checkbox" checked={rs.vocWaived}
                            onChange={(ev) => patch(e.enrollment_id, {
                              vocWaived: ev.target.checked,
                              ...(ev.target.checked ? {} : { vocWaiverReason: '' }),
                            })}
                            className="rounded border-gray-300" />
                          Waive VOC for this producer
                        </label>
                        <input
                          value={rs.vocWaiverReason}
                          disabled={!rs.vocWaived}
                          onChange={(ev) => patch(e.enrollment_id, { vocWaiverReason: ev.target.value })}
                          placeholder="Reason (required when waiving)"
                          className={inputCls + ' flex-1 min-w-[16rem] disabled:bg-gray-50 disabled:text-gray-400'
                            + (rs.vocWaived && !rs.vocWaiverReason.trim() ? ' border-amber-400' : '')} />
                        {rs.vocWaived && (
                          <span className="text-xs text-amber-700">
                            VOC stays outstanding for this farm — recorded as waived, not satisfied.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {result && !result.success && result.error && (
                  <div className="mt-2 text-xs text-red-600">{result.error}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Small({ children }: { children: ReactNode }) {
  return <span className="block text-[11px] font-medium text-gray-600 mb-0.5">{children}</span>;
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
      <ArrowLeft className="w-4 h-4" /> All enrollments
    </button>
  );
}
